/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from "react"
import { useLocation, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import CreateInstallationModal from "./components/CreateInstallationModal"
import DebugAccordion from "../../components/DebugAccordion"
import DownloadProgress from "../../components/DownloadProgress.jsx"
import Icon from "../../components/Icon"
import InstallationsInfoModal from './components/InstallationsInfoModal.jsx'
import LoadingAnimation from "../../components/LoadingAnimation.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"

import { fetchAgentManifest } from '../../state/actions/agentManifestActions'
import { fetchEphemeralRole } from '../../state/actions/roleActions'
import { createInstallation, deactivateInstallation, downloadInstallerPackage, fetchInstallationList } from '../../state/actions/installationActions'
import { NotificationTypes, addNotification } from "../../state/actions/notificationActions"
import { fetchProfileList } from '../../state/actions/profileActions'
import { createEphemeralUser } from '../../state/actions/userActions'
import { fetchUtgManifest } from '../../state/actions/utgManifestActions'

import { agentManifestSignal, agentManifestLoadedSignal, agentManifestErrorSignal } from '../../state/signals/agentManifestSignals'
import { utgManifestSignal, utgManifestLoadedSignal, utgManifestErrorSignal } from '../../state/signals/utgManifestSignals'
import { installationListSignal, installationListLoadedSignal, installationListErrorSignal } from '../../state/signals/installationSignals'
import { profileListSignal, profileListLoadedSignal, profileListErrorSignal } from '../../state/signals/profileSignals'

import { SortDirectionType } from "../../utils/constants.js"
import { computeSortedData } from "../../utils/table.js"
import { saveFile } from "../../utils/file.js"
import CopyRowAsJSON from "../../components/CopyRowAsJSON"
import PageSizeSelector from "../../components/PageSizeSelector.jsx"

export default function InstallationList() {

    const navigate = useNavigate()
    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewInstallations) {
            addNotification('You are not authorized to view installations for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditInstallations
    })

    useEffect(() => {
        fetchAgentManifest(config, accessToken)
        fetchUtgManifest(config, accessToken)
        fetchInstallationList(beId, config, accessToken)
        fetchProfileList(beId, config, accessToken)
    }, [beId, config, accessToken])

    const currentPageSignal = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize'), 10) || 250)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')

    const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const showDownloadProgressSignal = useSignal(false)
    const downloadProgressMessageSignal = useSignal('')
    
    const showInstallationsInfoModalSignal = useSignal(false)

    const showDeactivateModalSignal = useSignal(false)
    const installToDeactivateSignal = useSignal()
    const installToDeactivateNicknameSignal = useSignal('')

    const location = useLocation()
    let intent = location && location.state ? location.state.intent : null
    const intendToShowNewInstallationModalSignal = useSignal(intent == "install" || intent == "connect")
    intent = null

    const showNewInstallationModalComputed = useComputed(() => {
        // this will wait for all data to be loaded on the page first 
        return intendToShowNewInstallationModalSignal.value &&
            utgManifestLoadedSignal.value &&
            agentManifestLoadedSignal.value
    })

    const sortedProfilesComputed = useComputed(() => {
        if (!profileListLoadedSignal.value) {
            return []
        }
        const sorted = profileListSignal.value.sort((profile1, profile2) => {
            if (profile1.name.toLowerCase() < profile2.name.toLowerCase()) {
                return -1
            }
            else if (profile1.name == profile2.name) {
                return 0
            }
            return 1
        })
        return sorted
    })

    const filteredDataComputed = useComputed(() => {
        if (!filterStringSignal.value) {
            return installationListSignal.value ? [...installationListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return installationListSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                nickname: item.manifest.machine.nickname,
                verificationId: item.verificationId,
                profileId: item.profileId ? item.profileId.toString() : '',
                utgInstallLocation: item.manifest.components[0].installLocation,
                agentInstallLocation: item.manifest.agent.installLocation
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataComputed = useComputed(computeSortedData(filteredDataComputed, sortColumnSignal, sortDirectionSignal))

    const totalPagesComputed = useComputed(() => {
        return Math.ceil(filteredDataComputed.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataComputed = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedDataComputed.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberComputed = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberComputed = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataComputed.value.length ? endIndex : filteredDataComputed.value.length
    })

    const accordionData = [
        { name: 'Installations', data: installationListSignal.valuje },
        { name: 'Display Data', data: displayDataComputed.value },
        { name: 'Sorted Data', data: sortedDataComputed.value },
        { name: 'Filtered Data', data: filteredDataComputed.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'AvailableUtgs', data: utgManifestSignal.value },
        { name: 'AvailableAgents', data: agentManifestSignal.value }
    ]

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }

    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    const handleDeactivate = item => {
        if (item) {
            installToDeactivateSignal.value = item
            showDeactivateModalSignal.value = true
            installToDeactivateNicknameSignal.value = installToDeactivateSignal.value.manifest.machine.nickname
            console.log('handleDeactivate', installToDeactivateSignal.value)
        }
    }

    const deactivateInstall = async () => {
        showDeactivateModalSignal.value = false
        deactivateInstallation(config, installToDeactivateSignal.value.businessEntityId, installToDeactivateSignal.value.id, accessToken).then(
            () => {
                addNotification('Install successfully deactivated', NotificationTypes.SUCCESS, true)
                fetchInstallationList(beId, config, accessToken)
            }
        ).catch(
            (error) => {
                console.log(error)
                addNotification(`Unable to deactivate installation. ${error}`, NotificationTypes.ERROR, true)
            }
        )

    }

    // Show notification when user clicks on download link
    const handleDownloadInstaller = () => {
        showDownloadProgressSignal.value = true
        downloadProgressMessageSignal.value = 'Requesting installer file'
        setTimeout(() => {
            showDownloadProgressSignal.value = false
            addNotification('Downloading installer file. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
        }, 2000);
    }

    const installerDownloadUrlComputed = useComputed(() => {
        return config.installerDownloadUrl
    })

    const handleDownloadPackage = async (item) => {
        // set showDownloadProgress to true to show spinner
        showDownloadProgressSignal.value = true
        downloadProgressMessageSignal.value = 'Preparing to download installer package'
        if (item) {
            downloadInstallerPackage(config, item.verificationId).then(
                (installerPackageResponse) => {
                    saveFile(installerPackageResponse, "commandcenter.zip")
                    addNotification('Downloading installer package. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
                    showDownloadProgressSignal.value = false
                }
            ).catch(
                (error) => {
                    console.log(error)
                    addNotification(`Unable to download installer package. ${error}`, NotificationTypes.ERROR, true)
                    showDownloadProgressSignal.value = false
                }
            )
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    // Create Installation Logic
    const installationManifestObjectSignal = useSignal({})
    const gaUtgComputed = useComputed(() => {
        if (utgManifestLoadedSignal.value) {
            const filteredUtgs = utgManifestSignal.value.filter(
                (a) => {
                    return a.generalAvailability === true
                }
            )
            return filteredUtgs[0].version
        }
    })

    const gaAgentComputed = useComputed(() => {
        if (agentManifestLoadedSignal.value) {
            const filteredAgents = agentManifestSignal.value.filter(
                (a) => {
                    return a.generalAvailability === true
                }
            )
            return filteredAgents[0].version
        }
    })
    const handleShowInstallationModal = () => {
        intendToShowNewInstallationModalSignal.value = true
    }

    const handleCreateInstallation = async () => {
        intendToShowNewInstallationModalSignal.value = false
        showDownloadProgressSignal.value = true
        downloadProgressMessageSignal.value = 'Creating Installation'

        // Access user data retrieved using AccessToken

        if (!user) {
            window.alert("User is undefined")
            return
        }

        const currentUserId = user.id
        const currentDate = new Date()
        const lasUpdatedAt = currentDate.toISOString()
        const startDate = currentDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        currentDate.setFullYear(currentDate.getFullYear() + 99)
        const endDate = currentDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        // Fetch the ephemeral role id
        fetchEphemeralRole(beId, config, accessToken).then(
            async (ephemeralRoleResponse) => {
                const ephemeralRoleId = ephemeralRoleResponse.objectRoleMapping[0].roleId
                const ephemeralUserRequestBody = {
                    "authenticationCode": null,
                    "applicationId": 4,
                    "createdBy": `${currentUserId}`,
                    "allowDuplicateSessions": true,
                    "isRevoked": false,
                    "lastUpdatedAt": `${lasUpdatedAt}`,
                    "lastUpdatedBy": 1,
                    "businessEntityId": `${beId}`,
                    "lifespan": {
                        "lifeSpanType": 20,
                        "startDate": `${startDate}`,
                        "endDate": `${endDate}`
                    },
                    "roleMappings": [
                        {
                            "lastUpdatedAt": `${lasUpdatedAt}`,
                            "lastUpdatedBy": 1,
                            "roleId": `${ephemeralRoleId}`,
                            "businessEntityId": `${beId}`,
                            "ephemeralUserId": null
                        }
                    ],
                    "userSessions": []
                }

                // Create the ephermal user and set the returned values to use for create installation request
                createEphemeralUser(beId, config, ephemeralUserRequestBody, accessToken).then(
                    async (createEphemeralUserResponse) => {
                        const ephemeralUserAuthenticationCode = createEphemeralUserResponse.ephemeralUser.authenticationCode
                        const ephemeralUsersessionToken = createEphemeralUserResponse.ephemeralUser.userSessions[0].sessionToken
                        installationManifestObjectSignal.value.manifest.credentials.authenticationCode = ephemeralUserAuthenticationCode
                        installationManifestObjectSignal.value.manifest.credentials.sessionToken = ephemeralUsersessionToken

                        // Create the install and download the installer exe
                        createInstallation(beId, config, installationManifestObjectSignal.value, accessToken).then(
                            async (createInstallationResponse) => {
                                showDownloadProgressSignal.value = false
                                const formattedVerificationId = formatVerificationId(createInstallationResponse)
                                const installationName = createInstallationResponse.installation.manifest.machine.nickname
                                const notificationMessage = <span>The installation package has been created. Your Verification ID is {formattedVerificationId}. Write down the verification ID as it will be required during the installation process. The package can be downloaded <a href={installerDownloadUrlComputed.value}>here</a>.</span>
                                addNotification(notificationMessage, NotificationTypes.SUCCESS, true, `Installation ${installationName} created. Verification ID: ${formattedVerificationId}`)
                                fetchInstallationList(beId, config, accessToken)
                            }
                        ).catch(
                            (error) => {
                                addNotification(`Error creating installation: ${error.message}`, NotificationTypes.ERROR)
                            }
                        )
                    }
                ).catch(
                    (error) => {
                        addNotification(`Error creating ephemeral user: ${error.message}`, NotificationTypes.ERROR)
                    }
                )
            }
        ).catch(
            (error) => {
                addNotification(`Error fetching ephemeral role: ${error.message}`, NotificationTypes.ERROR)
            }
        )
    }

    const formatVerificationId = (createInstallationResponse) => {
        // Check if the object structure exists and contains an 8-character string
        if (
            typeof createInstallationResponse.installation.verificationId === 'string' &&
            createInstallationResponse.installation.verificationId.length === 8
        ) {
            // Insert a space in the middle (4th and 5th character)
            const verificationId = createInstallationResponse.installation.verificationId;
            return verificationId.slice(0, 4) + " " + verificationId.slice(4);
        }

        // Return null or an error message if the input is invalid
        return "Error formatting installation verification id";
    }

    function TableRows({ items }) {
        if (!installationListLoadedSignal.value) {
            return <tr><td colSpan={9}><LoadingAnimation /></td></tr>
        }
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return rows
        }
        return <tr><td colSpan="9" className="text-center">No installations found</td></tr>
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON
                                item={item}
                                messageClosure={
                                    (item) => {
                                        return `The data for ${item.manifest.machine.nickname} has been copied to your clipboard.`
                                    }
                                }
                            />
                            {canEditComputed.value && <Dropdown.Item onClick={() => handleDeactivate(item)}>Deactivate Install</Dropdown.Item>}
                            <Dropdown.Item as="a" href={installerDownloadUrlComputed.value} download onClick={() => handleDownloadInstaller()}>Download Installer</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDownloadPackage(item)}>Download Install Package</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.manifest.machine.nickname}</td>
                <td>{item.type == 10 ? 'New UTG' : 'Existing UTG'}</td>
                <td>{item.verificationId}</td>
                <td>{item.businessEntityId}</td>
                <td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
                <td>{item.profileId}</td>
                <td>{item.manifest?.components[0].installLocation}</td>
                <td>{item.manifest?.agent?.installLocation}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesComputed.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    return (
        <>
            <DownloadProgress showSignal={showDownloadProgressSignal} messageSignal={downloadProgressMessageSignal} />
            <ConfirmationModal show={showDeactivateModalSignal.value} handleCancel={() => showDeactivateModalSignal.value = false} handleConfirm={() => { deactivateInstall() }} message={`Are you sure you want to deactivate install: ${installToDeactivateNicknameSignal.value}? This action cannot be undone.`} title={"Deactivating Install"} />
            {showNewInstallationModalComputed.value && (
                <CreateInstallationModal
                    show={showNewInstallationModalComputed.value}
                    handleCancel={() => intendToShowNewInstallationModalSignal.value = false}
                    handleConfirm={() => handleCreateInstallation()}
                    businessEntityId={beId}
                    availableAgentsVersions={agentManifestSignal}
                    availableProfiles={sortedProfilesComputed.value}
                    availableUtgVersions={utgManifestSignal}
                    installationManifestObject={installationManifestObjectSignal}
                    defaultUtgVersion={gaUtgComputed.value}
                    defaultAgentVersion={gaAgentComputed.value}
                    intent={intent}
                />
            )}
            <PageTitle title='Installation List' canEditSignal={canEditComputed} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item className="me-auto">
                    <PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
                </Nav.Item>
                <Nav.Item>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                <Nav.Item className="ms-2">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => { showInstallationsInfoModalSignal.value = true }}>
                        <Icon icon="info-circle" height={16} width={16}></Icon>&nbsp;Help
                    </Button>
                </Nav.Item>
                {canEditComputed.value && <Nav.Item className="ms-2">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={handleShowInstallationModal}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Installation Name'} sortable={true} datafield={'manifest.machine.nickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Installation Type'} sortable={true} datafield={'type'}></TableColumnHeader>
                        <TableColumnHeader title={'Verification ID'} sortable={true} datafield={'verificationId'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
                        <TableColumnHeader title={'Creation Date'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile'} sortable={true} datafield={'profileId'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Install Location'} sortable={true} datafield={'manifest.components[0].installLocation'}></TableColumnHeader>
                        <TableColumnHeader title={'Agent Install Location'} sortable={true} datafield={'manifest.agent.installLocation'}></TableColumnHeader>
                    </tr>
                </thead>
                <tbody><TableRows items={displayDataComputed.value} /></tbody>
                <tfoot><tr><td className="fs-6" colSpan={19}>{
                    filteredDataComputed.value.length > 0 && (
                        <>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}</>
                    )
                }</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <InstallationsInfoModal
                userCanEditIn4mUtgInstallations={canEditComputed}
                showSignal={showInstallationsInfoModalSignal}
                handleCancel={() => showInstallationsInfoModalSignal.value = false}
                referenceGuid={config && config.referenceGuid ? config.referenceGuid : null}
            ></InstallationsInfoModal>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}